import { useLiveQuery } from 'dexie-react-hooks';
import {
  achievementScope,
  activeModules,
  allowedChildModules,
  childLevelOf,
  childMayUseNutrition,
  childModules,
  MODULES,
} from '@kraftwerk/shared';
import type { AchievementScope, ChildLevel, Module } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { readProfile } from '../db/profile.js';
import { currentOwnerId, currentRole } from '../auth.js';

/**
 * Die eingeschalteten Module (E15), live aus dem Profil. Solange das Profil
 * lädt: alles — sonst blinkten beim Start Reiter weg und wieder her.
 */
export function useModules(): Module[] {
  const stored = useLiveQuery(async () => (await readProfile(db)).modules, [], undefined);
  const child = useIsChild();
  const access = useChildAccess();
  if (child) return childModules(stored ?? null, access);
  return stored === undefined ? [...MODULES] : activeModules(stored);
}

/**
 * Was das angemeldete Kind darf (E20, 27./28.10.2026): Stufe und Freigaben
 * für Ernährung und Training, live aus der eigenen Mitgliedschaft — `null`
 * bei Erwachsenen.
 * Die Grenze zieht der Server; die App richtet nur Reiter und Felder danach.
 */
export function useChildAccess(): {
  level: ChildLevel;
  nutrition: boolean;
  training: boolean;
} | null {
  const child = useIsChild();
  const me = currentOwnerId();
  const stored = useLiveQuery(
    async () =>
      child && me
        ? ((await db.household_members.where('userId').equals(me).toArray()).find(
            (member) => !member.deletedAt,
          ) ?? null)
        : null,
    [child, me],
    null,
  );
  if (!child) return null;
  return {
    level: childLevelOf(stored?.childLevel),
    nutrition: stored?.teenNutrition === true,
    training: stored?.teenTraining === true,
  };
}

/** Die Module, die das Konto überhaupt einschalten kann — für Kinder weniger. */
export function useAvailableModules(): readonly Module[] {
  const access = useChildAccess();
  return access ? allowedChildModules(access) : MODULES;
}

/** Ist das Konto ein Teen mit Ernährung (27.10.2026)? Dann ohne Körperzahlen. */
export function useTeenNutrition(): boolean {
  return childMayUseNutrition(useChildAccess());
}

/**
 * Ist das angemeldete Konto ein Kind (E16)? Der Server setzt die Rolle bei
 * jeder Sitzungsprüfung; das Gerät merkt sie sich mit dem Konto. Die App
 * blendet danach Training, Ernährung, Kosten und alles Körperliche aus.
 */
export function useIsChild(): boolean {
  return currentRole() === 'child';
}

/** Die rohe Wahl — `null` heißt „nie gewählt“ (alles an). */
export function useStoredModules(): string[] | null | undefined {
  return useLiveQuery(async () => (await readProfile(db)).modules, [], undefined);
}

/**
 * Stufe des angemeldeten Kindes (E20), live aus der eigenen Mitgliedschaft —
 * `null` bei Erwachsenen. Was ein kleines Kind nicht schreiben darf, lehnt
 * der Server ab; die App zeigt die Knöpfe dafür gar nicht erst.
 */
export function useChildLevel(): ChildLevel | null {
  return useChildAccess()?.level ?? null;
}

/** Darf das Konto Termine, Aufgaben und Einkauf anlegen? Kleine Kinder nicht (E20). */
export function useMayPlan(): boolean {
  return useChildLevel() !== 'small';
}

/**
 * Welche Erfolge das Konto sieht (E13, für Teens seit 28.10.2026) — dieselbe
 * Regel wie der Scheduler (`achievementScope`). Ob sie überhaupt an sind,
 * steht zusätzlich im Profil (`achievements`).
 */
export function useAchievementScope(): AchievementScope {
  return achievementScope(useChildAccess(), useModules());
}
